import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { fetchContracts } from '../../api/contract';
import { fetchQcAnalyticsSummary } from '../../api/qcAnalytics';
import { useCompanyStore } from '../../store/companyStore';
import { Contract } from '../../types/contract';
import { QcAnalyticsSummary, QcNamedCount, QcNcrDrillDimension } from '../../types/qcAnalytics';
import { notifyError } from '../../utils/notify';
import './QualityShell.css';
import '../product/NonconformanceReportPanel.css';
import './QcAnalyticsPanel.css';
import QcNcrDrillModal, { QcNcrDrillTarget } from './QcNcrDrillModal';

type StratTab = 'source' | 'disposition' | 'item';
type ParetoMode = 'count' | 'qty';

const CHART_BLUE = '#1a5fb4';
const CHART_BLUE_SOFT = '#60a5fa';
const CHART_GRAY = '#64748b';
const CHART_LINE = '#0f766e';
const CHART_ORANGE = '#c2410c';

/** 기본 조회 기간(개월) */
const DEFAULT_RANGE_MONTHS = 12;
/** 최대 조회 기간(개월) — 백엔드와 동일 */
const MAX_RANGE_MONTHS = 24;

type ChartRow = {
  key: string;
  name: string;
  count: number;
  qtySum: number;
  percent: number;
  cumulativePercent: number;
};

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthsAgoIso(months: number) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoDate(value: string) {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** 시작~종료가 최대 개월 수를 넘으면 안내 메시지 반환 */
function validateRange(from: string, to: string): string | null {
  const fromDate = parseIsoDate(from);
  const toDate = parseIsoDate(to);
  if (!fromDate || !toDate) return '조회 기간을 확인해 주세요.';
  if (fromDate.getTime() > toDate.getTime()) return '시작일은 종료일 이전이어야 합니다.';
  const limit = new Date(fromDate);
  limit.setMonth(limit.getMonth() + MAX_RANGE_MONTHS);
  if (limit.getTime() < toDate.getTime()) {
    return `조회 기간은 최대 ${MAX_RANGE_MONTHS}개월까지입니다.`;
  }
  return null;
}

function toNum(value?: number | string | null) {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatQty(value?: number | string | null) {
  const n = toNum(value);
  return n.toLocaleString('ko-KR', { maximumFractionDigits: 2 });
}

function chartRows(items: QcNamedCount[] | undefined): ChartRow[] {
  return (items || []).map((row) => ({
    key: row.key || '',
    name: row.label || row.key || '미분류',
    count: row.count || 0,
    qtySum: toNum(row.qtySum),
    percent: toNum(row.percent),
    cumulativePercent: toNum(row.cumulativePercent),
  }));
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return message?.trim() || fallback;
}

function contractOptionLabel(row: Contract) {
  const no = row.contractNo || row.documentNo;
  return no ? `${row.contractName} (${no})` : row.contractName;
}

export default function QcAnalyticsPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const [from, setFrom] = useState(() => monthsAgoIso(DEFAULT_RANGE_MONTHS));
  const [to, setTo] = useState(() => todayIso());
  const [contractId, setContractId] = useState<number | ''>('');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<QcAnalyticsSummary | null>(null);
  const [stratTab, setStratTab] = useState<StratTab>('source');
  const [paretoMode, setParetoMode] = useState<ParetoMode>('count');
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillTarget, setDrillTarget] = useState<QcNcrDrillTarget | null>(null);

  useEffect(() => {
    if (!companyId) {
      setContracts([]);
      return;
    }
    let cancelled = false;
    void fetchContracts()
      .then((list) => {
        if (!cancelled) setContracts(list.filter((c) => !c.deletedAt));
      })
      .catch(() => {
        if (!cancelled) setContracts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    const rangeError = validateRange(from, to);
    if (rangeError) {
      setError(rangeError);
      notifyError(rangeError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchQcAnalyticsSummary({
        from,
        to,
        contractId: contractId === '' ? null : contractId,
      });
      setSummary(data);
      if (data.from) setFrom(data.from);
      if (data.to) setTo(data.to);
    } catch (err) {
      const msg = apiErrorMessage(err, 'QC 분석을 불러오지 못했습니다.');
      setError(msg);
      notifyError(msg);
    } finally {
      setLoading(false);
    }
  }, [companyId, from, to, contractId]);

  useEffect(() => {
    void load();
    // initial + company change only; refresh button for filter apply
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const openDrill = useCallback((target: QcNcrDrillTarget) => {
    setDrillTarget(target);
    setDrillOpen(true);
  }, []);

  const openNamedDrill = useCallback(
    (dimension: QcNcrDrillDimension, row: ChartRow | undefined, chartTitle: string) => {
      if (!row) return;
      const key = row.key || 'OTHER';
      openDrill({
        title: `${chartTitle} · ${row.name}`,
        subtitle: `${from} ~ ${to} · 클릭한 항목의 NCR 목록`,
        dimension,
        key,
      });
    },
    [openDrill, from, to]
  );

  const stratDimension: QcNcrDrillDimension =
    stratTab === 'source' ? 'sourceType' : stratTab === 'disposition' ? 'disposition' : 'itemName';
  const stratTitle =
    stratTab === 'source' ? '발생원' : stratTab === 'disposition' ? '조치' : '품명';

  const paretoData = useMemo(
    () =>
      chartRows(
        paretoMode === 'qty'
          ? summary?.defectParetoByQty || summary?.defectPareto
          : summary?.defectPareto
      ),
    [summary, paretoMode]
  );
  const fishboneData = useMemo(() => chartRows(summary?.causeFishbone), [summary]);
  const stratData = useMemo(() => {
    if (!summary) return [];
    if (stratTab === 'source') return chartRows(summary.bySourceType);
    if (stratTab === 'disposition') return chartRows(summary.byDisposition);
    return chartRows(summary.byItemName);
  }, [summary, stratTab]);

  const inspectionPassData = useMemo(
    () =>
      (summary?.inspectionPassRates || []).map((row) => ({
        key: row.key,
        name: row.label || row.key,
        passRatePct: toNum(row.passRatePct),
        passCount: row.passCount || 0,
        failCount: row.failCount || 0,
        totalCount: row.totalCount || 0,
      })),
    [summary]
  );

  const complaintTrendData = useMemo(
    () =>
      (summary?.complaintAsTrend || []).map((p) => ({
        period: p.period,
        complaintCount: p.complaintCount || 0,
        asCount: p.asCount || 0,
      })),
    [summary]
  );

  const checkSheet = summary?.checkSheet;
  const checkSheetMax = useMemo(() => {
    if (!checkSheet?.cells?.length) return 1;
    return Math.max(1, ...checkSheet.cells.map((c) => c.count || 0));
  }, [checkSheet]);

  const histogramData = useMemo(
    () =>
      (summary?.histogram?.bins || []).map((b) => ({
        label: b.label,
        count: b.count || 0,
      })),
    [summary]
  );

  const scatterData = useMemo(
    () =>
      (summary?.scatter?.points || []).map((p) => ({
        period: p.period,
        x: toNum(p.x),
        y: toNum(p.y),
        ncrCount: p.ncrCount || 0,
        defectRatePct: toNum(p.defectRatePct),
      })),
    [summary]
  );

  const controlData = useMemo(() => {
    const chart = summary?.controlChart;
    if (!chart) return [];
    const mean = toNum(chart.mean);
    const ucl = toNum(chart.ucl);
    const lcl = toNum(chart.lcl);
    return (chart.points || []).map((p) => ({
      period: p.period,
      value: toNum(p.value),
      mean,
      ucl,
      lcl,
      outOfControl: !!p.outOfControl,
    }));
  }, [summary]);

  const cellCount = (key: string, period: string) => {
    const cell = checkSheet?.cells?.find((c) => c.key === key && c.period === period);
    return cell?.count || 0;
  };

  const heatBg = (count: number) => {
    if (count <= 0) return '#fff';
    const t = Math.min(1, count / checkSheetMax);
    const alpha = 0.12 + t * 0.72;
    return `rgba(26, 95, 180, ${alpha.toFixed(3)})`;
  };

  const ncrTrendData = useMemo(
    () =>
      (summary?.ncrTrend || []).map((p) => ({
        period: p.period,
        ncrCount: p.ncrCount || 0,
        qtySum: toNum(p.qtySum),
      })),
    [summary]
  );

  const productionTrendData = useMemo(
    () =>
      (summary?.productionTrend || []).map((p) => ({
        period: p.period,
        defectRatePct: toNum(p.defectRatePct),
        goodQtySum: toNum(p.goodQtySum),
        defectQtySum: toNum(p.defectQtySum),
      })),
    [summary]
  );

  const periodLabel =
    summary?.from && summary?.to
      ? `${summary.from} ~ ${summary.to}`
      : from && to
        ? `${from} ~ ${to}`
        : '-';

  const drillQuery = useMemo(
    () => ({
      from: summary?.from || from,
      to: summary?.to || to,
      contractId: contractId === '' ? null : contractId,
    }),
    [summary?.from, summary?.to, from, to, contractId]
  );

  return (
    <main className="quality-shell">
      <div className="quality-shell__toolbar">
        <div>
          <h2>QC 분석</h2>
          <p>
            NCR·검사·불만/AS·생산일보 기반 QC 분석 · 기본 {DEFAULT_RANGE_MONTHS}개월, 최대{' '}
            {MAX_RANGE_MONTHS}개월
          </p>
        </div>
      </div>

      <div className="qc-filters">
        <label>
          시작일
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          종료일
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="qc-filters__contract">
          계약
          <select
            value={contractId === '' ? '' : String(contractId)}
            onChange={(e) => setContractId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">전체 계약</option>
            {contracts.map((row) => (
              <option key={row.contractId} value={row.contractId}>
                {contractOptionLabel(row)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="ncr-btn is-primary" onClick={() => void load()} disabled={loading}>
          {loading ? '검색 중…' : '검색'}
        </button>
      </div>

      {error && <p className="quality-error">{error}</p>}

      <div className="qc-kpi">
        <button
          type="button"
          className="qc-kpi__card qc-kpi__card--clickable"
          onClick={() =>
            openDrill({
              title: '기간 전체 NCR',
              subtitle: `${periodLabel} · 조회 조건의 전체 NCR`,
              dimension: null,
              key: null,
            })
          }
        >
          <p className="qc-kpi__label">NCR 건수</p>
          <p className="qc-kpi__value">{summary?.totalNcrCount ?? (loading ? '…' : 0)}</p>
        </button>
        <div className="qc-kpi__card">
          <p className="qc-kpi__label">NCR 수량합</p>
          <p className="qc-kpi__value">{summary ? formatQty(summary.totalQty) : loading ? '…' : '0'}</p>
        </div>
        <div className="qc-kpi__card">
          <p className="qc-kpi__label">불만</p>
          <p className="qc-kpi__value">{summary?.totalComplaintCount ?? (loading ? '…' : 0)}</p>
        </div>
        <div className="qc-kpi__card">
          <p className="qc-kpi__label">AS</p>
          <p className="qc-kpi__value">{summary?.totalAsCount ?? (loading ? '…' : 0)}</p>
        </div>
        <div className="qc-kpi__card">
          <p className="qc-kpi__label">기간</p>
          <p className="qc-kpi__value" style={{ fontSize: 15, fontWeight: 600 }}>
            {periodLabel}
          </p>
        </div>
      </div>

      <div className="qc-grid">
        <section className="qc-card qc-card--wide">
          <div className="qc-card__head">
            <div>
              <h3 className="qc-card__title">파레토 (불량 유형)</h3>
              <p className="qc-card__hint">
                {paretoMode === 'qty' ? '수량 막대' : '건수 막대'} + 누적 비율 · 막대 클릭 시 NCR 목록
              </p>
            </div>
            <div className="qc-strat-tabs quality-tabs">
              <button
                type="button"
                className={`quality-tabs__btn${paretoMode === 'count' ? ' is-active' : ''}`}
                onClick={() => setParetoMode('count')}
              >
                건수
              </button>
              <button
                type="button"
                className={`quality-tabs__btn${paretoMode === 'qty' ? ' is-active' : ''}`}
                onClick={() => setParetoMode('qty')}
              >
                수량
              </button>
            </div>
          </div>
          {paretoData.length === 0 ? (
            <div className="qc-empty">표시할 NCR 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis
                    yAxisId="left"
                    allowDecimals={paretoMode === 'qty'}
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      const n = typeof value === 'number' ? value : Number(value);
                      if (name === '누적%') return [`${n.toFixed(1)}%`, name];
                      if (paretoMode === 'qty') return [formatQty(n), name];
                      return [n, name];
                    }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey={paretoMode === 'qty' ? 'qtySum' : 'count'}
                    name={paretoMode === 'qty' ? '수량' : '건수'}
                    fill={CHART_BLUE}
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      const row = (data?.payload ?? data) as ChartRow | undefined;
                      openNamedDrill('defectCategory', row, '불량 유형');
                    }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulativePercent"
                    name="누적%"
                    stroke={CHART_LINE}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">특성요인 (5M1E)</h3>
          <p className="qc-card__hint">원인 분류별 NCR 건수 · 막대 클릭 시 NCR 목록</p>
          {fishboneData.length === 0 ? (
            <div className="qc-empty">표시할 원인 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={fishboneData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    name="건수"
                    fill={CHART_BLUE_SOFT}
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      const row = (data?.payload ?? data) as ChartRow | undefined;
                      openNamedDrill('causeCategory', row, '원인 분류');
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">층별 분석</h3>
          <p className="qc-card__hint">발생원·조치·품명 · 막대 클릭 시 NCR 목록</p>
          <div className="qc-strat-tabs quality-tabs" style={{ marginBottom: 8 }}>
            <button
              type="button"
              className={`quality-tabs__btn${stratTab === 'source' ? ' is-active' : ''}`}
              onClick={() => setStratTab('source')}
            >
              발생원
            </button>
            <button
              type="button"
              className={`quality-tabs__btn${stratTab === 'disposition' ? ' is-active' : ''}`}
              onClick={() => setStratTab('disposition')}
            >
              조치
            </button>
            <button
              type="button"
              className={`quality-tabs__btn${stratTab === 'item' ? ' is-active' : ''}`}
              onClick={() => setStratTab('item')}
            >
              품명 Top10
            </button>
          </div>
          {stratData.length === 0 ? (
            <div className="qc-empty">표시할 층별 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart qc-chart--sm">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stratData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: CHART_GRAY }} interval={0} angle={-20} textAnchor="end" height={56} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    name="건수"
                    fill={CHART_BLUE}
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      const row = (data?.payload ?? data) as ChartRow | undefined;
                      openNamedDrill(stratDimension, row, stratTitle);
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">NCR 추이</h3>
          <p className="qc-card__hint">월별 NCR 건수 · 점 클릭 시 해당 월 NCR 목록</p>
          {ncrTrendData.length === 0 ? (
            <div className="qc-empty">표시할 추이 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ncrTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ncrCount"
                    name="NCR 건수"
                    stroke={CHART_BLUE}
                    strokeWidth={2}
                    dot={{ r: 4, cursor: 'pointer' }}
                    activeDot={{
                      r: 6,
                      cursor: 'pointer',
                      onClick: (_e: unknown, payload: unknown) => {
                        const period =
                          (payload as { payload?: { period?: string } })?.payload?.period ??
                          (payload as { period?: string })?.period;
                        if (!period) return;
                        openDrill({
                          title: `NCR 추이 · ${period}`,
                          subtitle: `${period} 월 NCR 목록`,
                          dimension: 'period',
                          key: period,
                        });
                      },
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">불량률 추이</h3>
          <p className="qc-card__hint">생산일보 기준 월별 불량률(%) · NCR 드릴다운 없음</p>
          {productionTrendData.length === 0 ? (
            <div className="qc-empty">표시할 생산일보 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={productionTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip formatter={(value) => [`${toNum(value as number).toFixed(2)}%`, '불량률']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="defectRatePct"
                    name="불량률(%)"
                    stroke={CHART_LINE}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">검사 합격률</h3>
          <p className="qc-card__hint">인수·중간·제품검사 overall 판정 (적합/부적합)</p>
          {inspectionPassData.every((r) => r.totalCount === 0) ? (
            <div className="qc-empty">표시할 검사 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={inspectionPassData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    formatter={(value, name, item) => {
                      const row = item?.payload as {
                        passCount?: number;
                        failCount?: number;
                        totalCount?: number;
                      };
                      if (name === '합격률') {
                        return [
                          `${toNum(value as number).toFixed(1)}% (적합 ${row?.passCount ?? 0} / 전체 ${row?.totalCount ?? 0})`,
                          name,
                        ];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="passRatePct"
                    name="합격률"
                    fill={CHART_LINE}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">불만·AS 추이</h3>
          <p className="qc-card__hint">월별 불만/AS 건수 (계약 필터 미적용)</p>
          {complaintTrendData.length === 0 ? (
            <div className="qc-empty">표시할 불만·AS 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={complaintTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="complaintCount"
                    name="불만"
                    stroke={CHART_ORANGE}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="asCount"
                    name="AS"
                    stroke={CHART_BLUE}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card qc-card--wide">
          <h3 className="qc-card__title">체크시트</h3>
          <p className="qc-card__hint">불량 유형 × 월 집계 · 셀 클릭 시 NCR 목록</p>
          {!checkSheet?.periods?.length || !checkSheet.rowKeys?.length ? (
            <div className="qc-empty">표시할 체크시트 데이터가 없습니다.</div>
          ) : (
            <div className="qc-checksheet-wrap">
              <table className="qc-checksheet">
                <thead>
                  <tr>
                    <th>불량 유형</th>
                    {checkSheet.periods.map((p) => (
                      <th key={p}>{p}</th>
                    ))}
                    <th>합계</th>
                  </tr>
                </thead>
                <tbody>
                  {checkSheet.rowKeys.map((key, idx) => {
                    const rowTotal = checkSheet.periods.reduce(
                      (sum, p) => sum + cellCount(key, p),
                      0
                    );
                    return (
                      <tr key={key}>
                        <th scope="row">{checkSheet.rowLabels[idx] || key}</th>
                        {checkSheet.periods.map((p) => {
                          const count = cellCount(key, p);
                          return (
                            <td key={`${key}-${p}`}>
                              <button
                                type="button"
                                className="qc-checksheet__cell"
                                style={{ background: heatBg(count) }}
                                disabled={count <= 0}
                                onClick={() =>
                                  openDrill({
                                    title: `체크시트 · ${checkSheet.rowLabels[idx] || key} · ${p}`,
                                    subtitle: `${p} · ${checkSheet.rowLabels[idx] || key}`,
                                    dimension: 'defectCategory',
                                    key,
                                  })
                                }
                              >
                                {count || ''}
                              </button>
                            </td>
                          );
                        })}
                        <td className="qc-checksheet__total">{rowTotal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">히스토그램</h3>
          <p className="qc-card__hint">
            {summary?.histogram?.metric === 'PDR_DEFECT_QTY_DAILY'
              ? '일별 생산불량 수량 분포 (NCR 수량 표본 부족 시)'
              : 'NCR 수량 분포'}
            {summary?.histogram
              ? ` · n=${summary.histogram.sampleSize}` +
                (summary.histogram.excludedCount
                  ? ` · 제외 ${summary.histogram.excludedCount}`
                  : '')
              : ''}
          </p>
          {histogramData.length === 0 ? (
            <div className="qc-empty">표시할 히스토그램 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={histogramData} margin={{ top: 8, right: 8, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: CHART_GRAY }}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={56}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <Tooltip />
                  <Bar dataKey="count" name="빈도" fill={CHART_BLUE_SOFT} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card">
          <h3 className="qc-card__title">산점도</h3>
          <p className="qc-card__hint">월별 양품 수량(X) vs 불량 수량(Y)</p>
          {scatterData.length === 0 ? (
            <div className="qc-empty">표시할 산점도 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    name="양품"
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="불량"
                    tick={{ fontSize: 11, fill: CHART_GRAY }}
                  />
                  <ZAxis range={[60, 60]} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => [formatQty(value as number), name === 'x' ? '양품' : name === 'y' ? '불량' : String(name)]}
                    labelFormatter={(_, payload) => {
                      const p = payload?.[0]?.payload as { period?: string } | undefined;
                      return p?.period || '';
                    }}
                  />
                  <Scatter name="월별" data={scatterData} fill={CHART_BLUE} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="qc-card qc-card--wide">
          <h3 className="qc-card__title">관리도 (c-chart)</h3>
          <p className="qc-card__hint">
            월별 NCR 건수 · CL=평균, UCL/LCL=평균±3√평균
            {summary?.controlChart
              ? ` · CL ${toNum(summary.controlChart.mean).toFixed(1)} / UCL ${toNum(summary.controlChart.ucl).toFixed(1)} / LCL ${toNum(summary.controlChart.lcl).toFixed(1)}`
              : ''}
          </p>
          {controlData.length === 0 ? (
            <div className="qc-empty">표시할 관리도 데이터가 없습니다.</div>
          ) : (
            <div className="qc-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={controlData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: CHART_GRAY }} />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine y={controlData[0]?.ucl} stroke="#b91c1c" strokeDasharray="4 4" label="UCL" />
                  <ReferenceLine y={controlData[0]?.mean} stroke={CHART_LINE} label="CL" />
                  <ReferenceLine y={controlData[0]?.lcl} stroke="#b91c1c" strokeDasharray="4 4" label="LCL" />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="NCR 건수"
                    stroke={CHART_BLUE}
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props as {
                        cx?: number;
                        cy?: number;
                        payload?: { outOfControl?: boolean };
                      };
                      if (cx == null || cy == null) return null;
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={payload?.outOfControl ? 5 : 3}
                          fill={payload?.outOfControl ? '#b91c1c' : CHART_BLUE}
                          stroke="#fff"
                          strokeWidth={1}
                        />
                      );
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      <QcNcrDrillModal
        open={drillOpen}
        target={drillTarget}
        query={drillQuery}
        onClose={() => {
          setDrillOpen(false);
          setDrillTarget(null);
        }}
      />
    </main>
  );
}
